import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  fetchKpis, fetchAgentsStatus, fetchActivity, fetchSlaBurn,
  fetchIncidentHeatmap, fetchMttrTrend, fetchPublicStatus
} from '@/lib/api';
import { useNexusStore } from '@/stores/nexusStore';
import { AgentStatusPanel } from '@/components/dashboard/AgentStatusPanel';
import { LiveActivityFeed } from '@/components/dashboard/LiveActivityFeed';
import { KpiCards } from '@/components/dashboard/KpiCards';
import { SlaBurnWidget } from '@/components/dashboard/SlaBurnWidget';
import { IncidentHeatMap } from '@/components/dashboard/IncidentHeatMap';
import { MttrChart } from '@/components/dashboard/MttrChart';
import { QuickActions } from '@/components/dashboard/QuickActions';
import { StatusDot } from '@/components/shared/StatusDot';
import { AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const STATUS_STYLES = {
  OPERATIONAL: { label: 'All Systems Operational', color: 'var(--nexus-success)', Icon: CheckCircle, bg: 'bg-[#0d3d1d] border-green-500/20' },
  DEGRADED: { label: 'Minor Degradation', color: 'var(--nexus-warning)', Icon: AlertTriangle, bg: 'bg-[#4d3000] border-amber-500/20' },
  PARTIAL_OUTAGE: { label: 'Partial Outage', color: 'var(--nexus-warning)', Icon: AlertTriangle, bg: 'bg-[#4d3000] border-amber-500/20' },
  MAJOR_OUTAGE: { label: 'Major Outage', color: 'var(--nexus-danger)', Icon: XCircle, bg: 'bg-[#4a1515] border-red-500/20' },
};

export default function CommandCenter() {
  const { setKpis, setActivityFeed, activityFeed } = useNexusStore();
  const token = localStorage.getItem('nexus_token');
  const [clock, setClock] = useState(new Date().toISOString().replace('T', ' ').slice(0, 19));

  // Live ticking clock
  useEffect(() => {
    const timer = setInterval(() => setClock(new Date().toISOString().replace('T', ' ').slice(0, 19)), 1000);
    return () => clearInterval(timer);
  }, []);

  const { data: kpiData } = useQuery({ queryKey: ['kpis'], queryFn: fetchKpis, refetchInterval: 15000 });
  const { data: agents } = useQuery({ queryKey: ['agents-status'], queryFn: fetchAgentsStatus, refetchInterval: 10000 });
  const { data: initialActivity } = useQuery({ queryKey: ['activity'], queryFn: () => fetchActivity(30), refetchOnWindowFocus: false });
  const { data: slaBurn } = useQuery({ queryKey: ['sla-burn'], queryFn: fetchSlaBurn, refetchInterval: 30000 });
  const { data: heatmap } = useQuery({ queryKey: ['incident-heatmap'], queryFn: fetchIncidentHeatmap, refetchInterval: 30000 });
  const { data: mttrData } = useQuery({ queryKey: ['mttr-trend'], queryFn: fetchMttrTrend, refetchInterval: 60000 });
  const { data: status } = useQuery({ queryKey: ['public-status'], queryFn: fetchPublicStatus, refetchInterval: 30000 });
  const { data: recentCritical } = useQuery({
    queryKey: ['recent-critical'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/dashboard/recent-critical`, { headers: { Authorization: `Bearer ${token}` } }); return data; },
    refetchInterval: 15000,
  });

  useEffect(() => { if (kpiData) setKpis(kpiData); }, [kpiData, setKpis]);
  useEffect(() => { if (initialActivity && activityFeed.length === 0) setActivityFeed(initialActivity); }, [initialActivity, activityFeed.length, setActivityFeed]);

  const statusConf = STATUS_STYLES[status?.overall_status] || STATUS_STYLES.OPERATIONAL;
  const StatusIcon = statusConf.Icon;
  const activeAgents = (agents || []).filter(a => a.status === 'PROCESSING');

  return (
    <div data-testid="command-center" className="w-full max-w-[1600px] mx-auto">
      {/* System Status Banner */}
      <div className={`flex items-center justify-between px-4 py-2 rounded-xl border mb-3 ${statusConf.bg}`}>
        <div className="flex items-center gap-2">
          <StatusIcon size={16} style={{ color: statusConf.color }} />
          <span className="text-xs font-semibold" style={{ color: statusConf.color }}>{statusConf.label}</span>
          {activeAgents.length > 0 && (
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[#0d3d4d] text-[var(--nexus-accent)] border border-cyan-500/30 ml-2">
              {activeAgents.length} agent{activeAgents.length > 1 ? 's' : ''} active: {activeAgents.map(a => a.name).join(', ')}
            </span>
          )}
        </div>
        <span className="font-mono text-[10px] text-[var(--nexus-text-muted)] tabular-nums">{clock} UTC</span>
      </div>

      {/* Recent Critical Incidents Strip */}
      {recentCritical?.length > 0 && (
        <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1">
          <span className="text-[10px] text-[var(--nexus-text-muted)] uppercase tracking-wider font-medium flex-shrink-0">Critical:</span>
          {recentCritical.map(inc => (
            <Link key={inc.id} to={`/incidents/${inc.id}`}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] hover:bg-white/[0.04] flex-shrink-0 transition-colors">
              <span className={`font-mono text-[10px] font-bold px-1 py-0 rounded badge-${inc.priority?.toLowerCase()}`}>{inc.priority}</span>
              <span className="font-mono text-[10px] text-[var(--nexus-primary-bright)]">{inc.id}</span>
              <span className="text-[10px] text-[var(--nexus-text-secondary)] max-w-[150px] truncate">{inc.title}</span>
              <StatusDot status={inc.status === 'RESOLVED' ? 'success' : 'processing'} pulse={inc.status !== 'RESOLVED'} size="sm" />
            </Link>
          ))}
        </div>
      )}

      {/* Page title */}
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Command Center</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Real-time operations overview \u2022 {(agents || []).length} agents \u2022 {kpiData?.total_open_tickets || 0} open tickets</p>
        </div>
      </div>

      {/* 3-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* LEFT COLUMN - 25% */}
        <div className="lg:col-span-3 flex flex-col gap-3">
          <AgentStatusPanel agents={agents || []} />
          <SlaBurnWidget entries={slaBurn || []} />
        </div>

        {/* CENTER COLUMN - 50% */}
        <div className="lg:col-span-6 flex flex-col gap-3">
          <KpiCards kpis={kpiData} />
          <IncidentHeatMap data={heatmap || []} />
          <MttrChart data={mttrData || []} />
        </div>

        {/* RIGHT COLUMN - 25% */}
        <div className="lg:col-span-3 flex flex-col gap-3">
          <LiveActivityFeed />
          <QuickActions />
        </div>
      </div>
    </div>
  );
}
